import { MainTemplate } from 'Components/Template/MainTemplate'
import { Box, Container, Stack, Skeleton, ThemeProvider, Modal, Typography, Dialog, DialogContent } from '@mui/material'
import { observer, useLocalObservable } from 'mobx-react-lite'
import styled from '@emotion/styled'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import pMinDelay from 'p-min-delay'
import Breadcrumb from 'Components/Elements/Checkout/Breadcrumb'
import StoreCheckout from 'GlobalStores/Checkout/StoreCheckout'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import CartEmpty from './CartEmpty'
import { runInAction, toJS } from 'mobx'
import StoreFormat from 'GlobalStores/Format/StoreFormat'
import Checkout from 'GlobalStores/Checkout/Country/Checkout'
import { CreditCardHelper } from 'Helpers/CreditCardHelper'
import { T, useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import StorePaymentStep from 'GlobalStores/Cores/Elements/StorePaymentStep'
import { ReactComponent as CheckedIcon } from '../Elements/Checkout/Assets/check-green.svg'
import { ReactComponent as TimesIcon } from '../Elements/Checkout/Assets/times.svg'
import Loader from '../Elements/Checkout/Assets/Loader.svg'
import { storeElement } from 'GlobalStores/StoreElement'
import { useSizeWindow } from 'Helpers/GlobalHelpers'
import { FormatAPIs } from 'Services/FeelGreat/FormatAPIs'
import StoreCountry from 'GlobalStores/Cores/County/StoreCountry'
import Errors from 'Components/Elements/Errors/Errors'
import { DefaultButton } from 'Components/Elements/Forms'
import { get, isEmpty } from 'lodash'
import StoreAuth from 'GlobalStores/User/StoreAuth'
import StoreUser from 'GlobalStores/User/StoreUser'
import DefaultCheckoutTheme from './CheckoutThemes/DefaultCheckoutTheme'
import PhillipinesCheckoutTheme from './CheckoutThemes/PhilippinesCheckoutTheme'
import MoroccoCheckoutTheme from './CheckoutThemes/MoroccoCheckoutTheme'
import JordanCheckoutTheme from './CheckoutThemes/JordanCheckoutTheme'
import TaiwanCheckoutTheme from './CheckoutThemes/TaiwanCheckoutTheme'
import JapanCheckoutTheme from './CheckoutThemes/JapanCheckoutTheme'
import TurkiyeCheckoutTheme from './CheckoutThemes/TurkiyeCheckoutTheme'
import EgyptCheckoutTheme from './CheckoutThemes/EgyptCheckoutTheme'
import WorldPay from 'Components/Elements/Checkout/WorldPay/WorldPay'
import SouthAfricaCheckoutTheme from './CheckoutThemes/SouthAfricaCheckoutTheme'
import ThailandCheckoutTheme from './CheckoutThemes/ThailandCheckoutTheme'
import KuwaitCheckoutTheme from './CheckoutThemes/KuwaitCheckoutTheme'
import SaudiarabiaCheckoutTheme from './CheckoutThemes/SaudiarabiaCheckoutTheme'
import StoreSubScriptions from 'GlobalStores/StoreSubScriptions'
import { PaymentAPIs } from 'GlobalStores/Checkout/PaymentServices/PaymentAPI'
import DuplicatedOrder from 'Components/Elements/DuplicatedOrder/DuplicatedOrder'
import httpHelper from 'Services/HttpHelper'
import { useCreditcardAliases } from 'Components/SavedCreditCard/Hooks/useCreditcardAliases'
import { storeCreditCardAlias } from 'Components/SavedCreditCard/Elements/CreditCardAliasBox'
import WorldPay3DS from 'Components/Elements/Checkout/WorldPay/WorldPay3DS'
import storeCredit from 'GlobalStores/StoreCredit'
import storeFeelGreat from 'GlobalStores/FeelGreat/StoreFeelGreat'
import HongkongCheckoutTheme from './CheckoutThemes/HongkongCheckoutTheme'

const ShippingAddressBoxLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/ShippingAddress'), 0))
const PaymentBoxLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/Payment'), 0))
const PaymentReviewLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/PaymentReview'), 0))
const CreateAccountWithShippingLazyLoad = lazy(() => pMinDelay(import('../Elements/Checkout/CreateAccountWithShipping'), 0))

const CheckoutPage = observer(() => {
    const { width } = useSizeWindow()
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const translate = useTranslate()
    const [openModal, setOpenModal] = useState(false)
    const [openLoginModal, setOpenLoginModal] = useState(false)
    const [openWorldPay, setOpenWorldPay] = useState(false)
    const [openBankWireModal, setOpenBankWireModal] = useState(false)
    const [openDuplicatedModal, setOpenDuplicatedModal] = useState(false)
    const [duplicatedData, setDuplicatedData] = useState({
        paymentId: '',
        dateAndTime: '',
        totalPrice: '',
        currency: '',
        is_duplicate: false
    })

    const { getRemainingTime } = useCreditcardAliases()

    const qs = useMemo(() => new URLSearchParams(window.location.search), [])

    const [hydraCreatePaymentResponseBody, setHydraCreatePaymentResponseBody] = useState(null)
    const [hydraCreateOrderResponse, setHydraCreateOrderResponse] = useState(null)
    const [hydraCreateOrderResponseHeader, setHydraCreateOrderHeader] = useState(null)
    const [hydraCreateOrderRequestBody, setHydraCreateOrderRequestBody] = useState(null)
    const [paymentId, setPaymentId] = useState(null)
    const [challengeHTML, setChallengeHTML] = useState(null)

    const localObserver = useLocalObservable(() => ({
        paymentId: null,
        hydraResponse: null,
        isTimeout: false
    }))

    function maskCreditCard(creditCardNumber) {
        if (creditCardNumber.length < 8) return creditCardNumber // Ensure valid length

        return creditCardNumber.slice(0, 6) + 'x'.repeat(6) + creditCardNumber.slice(-4)
    }

    const toCurl = ({
        method = `POST`,
        url,
        headers = {
            'Content-Type': `application/json`
        },
        dataRaw
    }) => {
        const curlLines = [`curl --location --request ${method} '${httpHelper.Proxy({ originalUrl: url })}'`, ...Object.keys(headers).map(key => `--header '${key}: ${headers[key]}'`), `--data-raw '${dataRaw}'`]

        return curlLines.join(` `)
    }

    const SaveCardAlias = async () => {
        // console.log('SaveCardAlias retry count=', storeCreditCardAlias.retryCount, 'remaining time=', storeCreditCardAlias.remainingTime)
        let cardAlias = ''
        const hasSubscribe = /subscribe/i.test(StoreFormat.GetDetailPage().shipment_packs)

        if (!hasSubscribe && !StoreCheckout.isSaveCreditCard) {
            return ''
        }
        const alias = maskCreditCard(GetCardValue('card_number'))
        const splitExpire = GetCardValue('exp_date').split('/')
        const cardDataRaw = {
            methodDetails: {
                alias: alias,
                creditCardNumber: GetCardValue('card_number'),
                creditCardExpires: `${Number(splitExpire[1]) + 2000}-${splitExpire[0]}`,
                payer: GetCardValue('name_on_card'),
                billToAddress: {
                    country: StoreCountry.Country2()
                }
            },
            billToAddress: {
                country: StoreCountry.Country2()
            }
        }
        const markedCardDataRaw = {
            ...cardDataRaw,
            methodDetails: {
                ...cardDataRaw.methodDetails,
                creditCardNumber: maskCreditCard(cardDataRaw.methodDetails.creditCardNumber)
            }
        }
        const HydraURL = /localhost/.test(window.location.href) ? 'https://hydra.unicity.net/v6' : 'https://hydra.unicity.net/v6'
        try {

            const response = await PaymentAPIs.SaveCardAlias(cardDataRaw, StoreUser.CustomerData().Country(), StoreAuth.GetToken())
            console.log('card response:', response)
            const href = response.data.href
            if (href) {
                const checkCardAlias = await PaymentAPIs.checkCardAlias(href, StoreAuth.GetToken())
                cardAlias = checkCardAlias.data.methodDetails.creditCardNumber || checkCardAlias.data.methodDetails.creditCardNumberMasked || checkCardAlias.data.methodDetails.alias

                if (!['AUS', 'NZL'].includes(StoreCountry.Country3())) {
                    const dataRaw = {
                        uuid: StoreCheckout.GetUuid(),
                        hydra_alias_detail_curl: toCurl({
                            method: `GET`,
                            url: checkCardAlias.config.url,
                            headers: {
                                'Content-Type': `application/json`,
                                Authorization: `Bearer ${StoreAuth.GetToken()}`
                            },
                            dataRaw: ''
                        }),
                        hydra_alias_detail_response_header: JSON.stringify(checkCardAlias.headers),
                        hydra_alias_detail_response_body: JSON.stringify(checkCardAlias.data)
                    }

                    if (StoreCountry.Country2() === 'SA') {
                        dataRaw.payment_options = StoreCheckout.GetPaymentMethod()
                    }
                    await PaymentAPIs.UpdateUUid(
                        dataRaw,
                        StoreCountry.Country3(),
                        true
                    )
                }
            }

            runInAction(() => {
                storeCreditCardAlias.setIsNewCard(true)
                StoreCheckout.saveCardAliasResponse.header = JSON.stringify(response.headers)
                StoreCheckout.saveCardAliasResponse.curl = toCurl({
                    url: `${HydraURL}/customers/me/creditcardaliases/${StoreUser.CustomerData().Country()}`,
                    dataRaw: JSON.stringify(markedCardDataRaw)
                })
                StoreCheckout.saveCardAliasResponse.response = JSON.stringify(response.data)
                storeCreditCardAlias.setIsNewCard(true)
            })

            return cardAlias
        } catch (e) {
            console.log('SaveCardAlias function error:', e)
            storeCreditCardAlias.setRetryCount(storeCreditCardAlias.retryCount + 1)
            if (storeCreditCardAlias.retryCount < storeCreditCardAlias.remainingTime) {
                // Retry the save operation
                return await SaveCardAlias()
            } else {
                StoreCheckout.saveCardAliasResponse.curl = toCurl({
                    url: `${HydraURL}/customers/me/creditcardaliases/${StoreUser.CustomerData().Country()}`,
                    dataRaw: JSON.stringify(markedCardDataRaw)
                })

                if (get(e, 'response.data')) {
                    // Server responded with a non-2xx status
                    console.error('Status:', e.response.status)
                    console.error('Data:', e.response.data)
                    StoreCheckout.saveCardAliasResponse.response = JSON.stringify(e.response.data)
                } else {
                    if (e.message) {
                        StoreCheckout.saveCardAliasResponse.response = JSON.stringify({ message: e.message })
                    } else {
                        StoreCheckout.saveCardAliasResponse.response = JSON.stringify(e)
                    }
                }
                if (e.request) {
                    // Request was made but no response received
                    if (get(e, 'request.responseText', '').length > 0) {
                        StoreCheckout.saveCardAliasResponse.header = JSON.stringify(e.request)
                    } else {
                        StoreCheckout.saveCardAliasResponse.header = JSON.stringify(e.config.headers)
                    }
                    console.error('No response:', e.request)
                } else {
                    StoreCheckout.saveCardAliasResponse.header = JSON.stringify(e.config.headers)
                }
                // Max retries reached, throw error to stop execution
                throw new Error('Failed to save card alias after maximum retries.', storeCreditCardAlias.retryCount)
            }
        }
    }

    const isOneTimePurchase = () => {
        let hasSubscribeInCart = false
        Object.keys(StoreCheckout.GetCart()).forEach(key => {
            if (key === 'subscribe') {
                hasSubscribeInCart = true
            }
        })
        return !/subscribe/i.test(StoreFormat.GetDetailPage().shipment_packs) || !hasSubscribeInCart
    }

    const submitPayment = async () => {
        try {
            StoreCheckout.checkoutLoading = false

            runInAction(() => {
                StoreCheckout.checkoutErrors = []
                storeElement.showCardFrontDrop = true
                StoreCheckout.checkoutLoading = true
            })

            const creditCard = {
                countryCode: StoreCountry.Country2(),
                creditCard: {
                    creditCardNumber: GetCardValue('card_number'),
                    payer: GetCardValue('name_on_card'),
                    creditCardExpires: GetCardValue('exp_date'),
                    creditCardSecurityCode: GetCardValue('cvv')
                }
            }

            const afterPassed = () => {
                setTimeout(() => {
                    runInAction(() => {
                        StoreCheckout.checkoutLoading = false
                        storeElement.showCardFrontDrop = true
                    })
                    navigate(`/checkout/review?uuid=${new URLSearchParams(location.search).get('uuid')}`)
                }, 1000)
            }

            const ProcessFormat = async () => {
                let cardAlias = ''

                if (StoreCheckout.isCountrySaveAlias.includes(StoreCountry.Country3())) {
                    let shouldSaveAlias = false

                    if (storeCreditCardAlias.getIsNewCard()) {
                        if (isOneTimePurchase()) {
                            if (StoreCheckout.isSaveCreditCard) {
                                shouldSaveAlias = true
                            }
                        } else {
                            shouldSaveAlias = true
                        }
                    }

                    const creditCardNumber = storeCreditCardAlias.getIsNewCard() ? creditCard.creditCard?.creditCardNumber : StoreCheckout.cardAlias


                    if (!/x/.test(creditCardNumber)) {
                        const isCreditCardValid = await CreditCardHelper.checkCreditCardByAPI(creditCardNumber)

                        if (!isCreditCardValid.success) {
                            runInAction(() => {
                                storeElement.showCardFrontDrop = false
                                StoreCheckout.checkoutErrors = [isCreditCardValid.error_message]
                                StoreCheckout.checkoutLoading = false
                            })
                            return
                        }
                    }

                    if (shouldSaveAlias) {
                        const alias = await SaveCardAlias()
                        cardAlias = alias
                    } else {
                        // shouldSaveAlias is false, Maybe user don't want to save alias.

                        cardAlias = StoreCheckout.cardAlias
                    }
                } else {
                    cardAlias = StoreCheckout.cardAlias
                }

                if (StoreCheckout.useBillingAddressSameAsShipping === false) {
                    let data = {
                        uuid: new URLSearchParams(location.search).get('uuid'),
                        ship_address_options: true
                    }
                    if (StoreCheckout.GetBillingAddressForm()) {
                        StoreCheckout.GetBillingAddressForm().map(item => {
                            if (item.key !== 'country') {
                                let key = item.key.toLowerCase()
                                if (key === 'zip_code') {
                                    key = 'zip'
                                }
                                if (key === 'street_address') {
                                    key = 'address'
                                }
                                data[`ship_address_${key}`] = item.value
                            }

                            return item
                        })

                        data['ship_address_fullname'] = StoreFormat.GetDetailPage().fullname
                        data['customer_main_address'] = StoreUser.CustomerData().Country()
                    }

                    if (cardAlias && params.step === 'payment' && !['AUS', 'NZL'].includes(StoreCountry.Country3())) {
                        data['alias_name'] = cardAlias
                        if (!isEmpty(StoreCheckout.saveCardAliasResponse.curl)) {
                            data['hydra_alias_curl'] = StoreCheckout.saveCardAliasResponse.curl
                            data['hydra_alias_header'] = StoreCheckout.saveCardAliasResponse.header
                            data['hydra_alias_response'] = StoreCheckout.saveCardAliasResponse.response
                        }
                    }

                    const updateResponse = await FormatAPIs.putHotFormatV2(data, StoreCountry.Country3(), cardAlias ? { isSkipValidate: 1 } : null)
                    if (updateResponse.data.success) {
                        afterPassed()
                    } else {
                        runInAction(() => {
                            storeElement.showCardFrontDrop = false
                            StoreCheckout.checkoutErrors = updateResponse.data.Message
                            StoreCheckout.checkoutLoading = false
                        })
                    }
                } else {
                    if (StoreFormat.GetDetailPage().ship_address_options || StoreCheckout.isCountrySaveAlias.includes(StoreCountry.Country3())) {
                        let data = {
                            uuid: new URLSearchParams(location.search).get('uuid'),
                            ship_address_options: false
                        }

                        if (cardAlias && params.step === 'payment' && !['AUS', 'NZL'].includes(StoreCountry.Country3())) {
                            data['alias_name'] = cardAlias
                            if (!isEmpty(StoreCheckout.saveCardAliasResponse.curl)) {
                                data['hydra_alias_curl'] = StoreCheckout.saveCardAliasResponse.curl
                                data['hydra_alias_header'] = StoreCheckout.saveCardAliasResponse.header
                                data['hydra_alias_response'] = StoreCheckout.saveCardAliasResponse.response
                            }
                        }

                        const updateResponse = await FormatAPIs.putHotFormatV2(data, StoreCountry.Country3(), cardAlias ? { isSkipValidate: 1 } : null)
                        if (updateResponse.data.success) {
                            afterPassed()
                        } else {
                            runInAction(() => {
                                storeElement.showCardFrontDrop = false
                                StoreCheckout.checkoutErrors = updateResponse.data.Message
                                StoreCheckout.checkoutLoading = false
                            })
                        }
                    } else {
                        afterPassed()
                    }
                }
            }

            if (params.step === 'payment') {
                if (StoreCheckout.GetPaymentMethod() === 'bankwire') {
                    if (!StoreAuth.GetToken()) {
                        await StoreAuth.Login({ unicityID: StoreUser.baId, password: StoreUser.password })
                    }
                    await ProcessFormat()
                } else {
                    if (!StoreAuth.GetToken()) {
                        await StoreAuth.Login({ unicityID: StoreUser.baId, password: StoreUser.password })
                    }
                    await ProcessFormat()
                }
            } else {
                if (StoreCheckout.GetPaymentMethod() === 'bankwire' && ['JPN'].includes(StoreCountry.Country3())) {
                    setOpenBankWireModal(true)
                    runInAction(() => {
                        StoreCheckout.checkoutLoading = true
                        StoreCheckout.checkoutErrors = []
                        StorePaymentStep.errors = []
                    })
                    const checkout = new Checkout()
                    let items = []

                    StoreFormat.GetFormatData('formatProducts').map(item => {
                        items.push({
                            item_id: item.item_code,
                            item_name: item.item_name.english + ' ' + item.item_name.english,
                            price: item.baStatus === 'C' ? item.retail_price_x_qty.value : item.price_x_qty.value
                        })
                    })

                    const response = await checkout.SubmitBankwirePayment(StoreCheckout.GetUuid(), StoreFormat.GetDetailPage(), items)
                    if (response.success) {
                        runInAction(() => {
                            storeElement.showCardFrontDrop = true
                            StoreCheckout.checkoutLoading = false
                        })
                        StoreCheckout.RestoreCart()
                        if (get(response, 'gateway_ref_id')) {
                            navigate(`/payment-response/success?gateway_ref_id=${response.gateway_ref_id}`)
                        } else {
                            navigate(`/payment-response/success?payment_id=${response.payment_id}`)
                        }
                    } else {
                        runInAction(() => {
                            storeElement.showCardFrontDrop = false
                            StoreCheckout.checkoutLoading = false
                        })
                        setOpenBankWireModal(false)
                    }
                } else {
                    const paymentChannel = await StoreCheckout.CheckOutInstance().GetPaymentChannel()

                    if (paymentChannel === 'hydraWorldPay') {
                        const hasSubscribe = /subscribe/i.test(StoreFormat.GetDetailPage().shipment_packs)
                        setOpenModal(true)
                        setOpenWorldPay(false)
                        runInAction(() => {
                            StoreCheckout.checkoutErrors = []
                            StorePaymentStep.errors = []
                            StorePaymentStep.SetPaymentStep('payment', 0)
                            StorePaymentStep.SetPaymentStep('worldPayReq', storeCreditCardAlias.getIsNewCard() ? 0 : -2)
                            StorePaymentStep.SetPaymentStep('createOrder', 0)
                            StorePaymentStep.SetPaymentStep('autoship', hasSubscribe ? 0 : -2)
                            StorePaymentStep.SetPaymentStep('updateLog', 0)
                        })
                        const checkout = new Checkout()
                        let items = []

                        StoreFormat.GetFormatData('formatProducts').map(item => {
                            items.push({
                                item_id: item.item_code,
                                item_name: item.item_name.english + ' ' + item.item_name.english,
                                price: item.baStatus === 'C' ? item.retail_price_x_qty.value : item.price_x_qty.value
                            })
                        })

                        const creditCardData = checkout.CreditCardEncrypted(creditCard.creditCard)

                        // const allowCreditEnable =  StoreAuth.IsAllowCredit()

                        // if (allowCreditEnable && storeCredit.GetCreditData()?.creditUsed > 0) {
                        //     creditCardData.amount = StoreCheckout.TotalPrice()
                        // }

                        const response = await checkout.SubmitPayment(
                            StoreCheckout.GetUuid(),
                            StoreFormat.GetDetailPage(),
                            creditCardData,
                            StoreCheckout.GetBillingAddressForm(),
                            items,
                            undefined,
                            undefined,
                            storeCreditCardAlias.getIsNewCard() ? undefined : StoreCheckout.cardAlias
                        )
                        // console.log('response hydraWorldPay', response)
                        if (response.needs3DS) {
                            setHydraCreateOrderResponse(response.hydraResponseBody)
                            setHydraCreateOrderHeader(response.hydraResponseHeader)
                            setHydraCreateOrderRequestBody(response.hydraRequestBody)
                            setHydraCreatePaymentResponseBody(response.hydraPaymentResponseBody)
                            setPaymentId(response.paymentId)
                            setChallengeHTML(response.challengeHTML)
                        } else if (response.success) {
                            runInAction(() => {
                                storeElement.showCardFrontDrop = true
                                StoreCheckout.checkoutLoading = false
                            })
                            setOpenModal(false)
                            setOpenWorldPay(false)
                            StoreCheckout.RestoreCart()
                            storeCreditCardAlias.setIsNewCard(false)
                            if (get(response, 'gateway_ref_id')) {
                                navigate(`/payment-response/success?gateway_ref_id=${response.gateway_ref_id}`)
                            } else {
                                navigate(`/payment-response/success?payment_id=${response.payment_id}`)
                            }
                        } else {
                            // ponytail: do NOT re-submit on failure — a failed
                            // ordersAndSubscriptions could still have created the order,
                            // and a blind retry double-charges the alias. Errors are
                            // already set by the PostToHydra catch path.
                            runInAction(() => {
                                StoreCheckout.checkoutLoading = false
                            })
                        }
                    } else {
                        setOpenModal(true)
                        runInAction(() => {
                            StoreCheckout.checkoutErrors = []
                            StorePaymentStep.errors = []
                        })
                        const checkout = new Checkout()
                        let items = []

                        StoreFormat.GetFormatData('formatProducts').map(item => {
                            items.push({
                                item_id: item.item_code,
                                item_name: item.item_name.english + ' ' + item.item_name.english,
                                price: item.baStatus === 'C' ? item.retail_price_x_qty.value : item.price_x_qty.value
                            })
                        })

                        const creditCardData = checkout.CreditCardEncrypted(creditCard.creditCard)

                        // const allowCreditEnable =  StoreAuth.IsAllowCredit()

                        // if (allowCreditEnable && storeCredit.GetCreditData()?.creditUsed > 0) {
                        //     creditCardData.amount = StoreCheckout.TotalPrice()
                        // }

                        const response = await checkout.SubmitPayment(
                            StoreCheckout.GetUuid(),
                            StoreFormat.GetDetailPage(),
                            creditCardData,
                            StoreCheckout.GetBillingAddressForm(),
                            items,
                            undefined,
                            undefined,
                            storeCreditCardAlias.getIsNewCard() ? undefined : StoreCheckout.cardAlias
                        )
                        // console.log('response', response)
                        if (response.success) {
                            runInAction(() => {
                                storeElement.showCardFrontDrop = true
                                StoreCheckout.checkoutLoading = false
                            })
                            setOpenModal(false)
                            setOpenWorldPay(false)
                            StoreCheckout.RestoreCart()
                            storeCreditCardAlias.setIsNewCard(false)
                            if (get(response, 'gateway_ref_id')) {
                                navigate(`/payment-response/success?gateway_ref_id=${response.gateway_ref_id}`)
                            } else {
                                navigate(`/payment-response/success?payment_id=${response.payment_id}`)
                            }
                        } else {
                            runInAction(() => {
                                storeElement.showCardFrontDrop = false
                                StoreCheckout.checkoutLoading = false
                            })
                        }
                    }
                }
            }
        } catch (e) {
            console.log('SaveCardAlias error:', e)
            if (/Failed to save card alias/.test(e)) {
                StoreCheckout.checkoutErrors = ['failed_to_save_card_alias']
            }

            runInAction(() => {
                storeElement.showCardFrontDrop = false
                StoreCheckout.checkoutLoading = false
            })
        }

        // try {
        //     await PaymentAPIs.UpdateUUid(
        //         {
        //             uuid: StoreCheckout.GetUuid(),
        //             hydra_alias_detail_curl: StoreCheckout.saveCardAliasResponse.curl,
        //             hydra_alias_detail_response_header: StoreCheckout.saveCardAliasResponse.header,
        //             hydra_alias_detail_response_body: StoreCheckout.saveCardAliasResponse.response
        //         },
        //         StoreCountry.Country3(),
        //         true
        //     )
        //     StoreCheckout.checkoutLoading = false
        // } catch (e) {
        //     console.log('UpdateUUid error:', e)
        // }
    }

    const GetCardValue = key => {
        const card = StoreCheckout.creditCard.find(item => item.label === key)
        if (card) {
            return card.value
        }
        return ''
    }

    const setCompletedActionsLink = () => {
        runInAction(() => {
            const idx = StoreCheckout.CheckOutInstance()
                .GetBreadcrumbOption()
                .findIndex(e => e.link.includes(`/checkout/${params.step}`))

            const result = StoreCheckout.CheckOutInstance()
                .GetBreadcrumbOption()
                .reduce((acc, current, index) => {
                    if (index > idx) return acc
                    if (current.link === '/products') {
                        return [...acc, current.link]
                    }

                    return [...acc, `${current.link}?uuid=${StoreCheckout.GetUuid()}`]
                }, [])

            // StoreCheckout.completedActionsLink.push(`/checkout/${params.step}?uuid=${StoreCheckout.GetUuid()}`)

            StoreCheckout.completedActionsLink.push(...result)
        })
    }

    const closeDuplicateModal = () => {
        setOpenDuplicatedModal(false)
        runInAction(() => {
            storeElement.showCardFrontDrop = false
        })
    }

    const confirmPlaceOrder = async () => {
        setOpenDuplicatedModal(false)
        runInAction(() => {
            StoreCheckout.paymentErrors = []
            storeElement.showCardFrontDrop = true
        })
        await submitPayment()
    }

    const onPlaceOrder = async () => {
        // console.log('on place order')
        const pathname = location.pathname
        const BreadcrumbStep = StoreCheckout.CheckOutInstance().GetBreadcrumbOption()
        const paymentStep = BreadcrumbStep.findIndex(item => item.link === pathname)
        const lastStep = paymentStep === BreadcrumbStep.length - 1

        if (lastStep) {
            const responseDuplicateOrder = await checkDuplicateOrder()

            if (responseDuplicateOrder.isDuplicate) {
                return
            } else {
                await submitPayment()
            }
        } else {
            await submitPayment()
        }
    }

    const checkDuplicateOrder = async () => {
        try {
            runInAction(() => {
                StoreCheckout.paymentErrors = []
                storeElement.showCardFrontDrop = true
            })
            const checkDuplicateOrder = await FormatAPIs.checkDuplicateOrder({
                countryCode3Alpha: StoreCountry.Country3(),
                uuid: StoreCheckout.GetUuid() || qs.get('uuid')
            })

            if (checkDuplicateOrder.data.is_duplicate === true) {
                setDuplicatedData({
                    paymentId: checkDuplicateOrder.data.payments_id,
                    dateAndTime: checkDuplicateOrder.data.date_time,
                    totalPrice: checkDuplicateOrder.data.total_price,
                    is_duplicate: true,
                    currency: checkDuplicateOrder.data.currency || StoreCountry.GetCurrencyCode()
                })
                setOpenDuplicatedModal(true)
            }

            return {
                isDuplicate: checkDuplicateOrder.data.is_duplicate
            }
        } catch (e) {
            console.log('error', e)
            closeDuplicateModal()
        }
    }

    useEffect(() => {
        setCompletedActionsLink()
        StoreCheckout.checkoutErrors = []
        runInAction(() => {
            // storeElement.showCardFrontDrop = false
            StoreCheckout.checkoutErrors = []
            StoreCheckout.checkoutLoading = false
            storeElement.showCardFrontDrop = false
        })
        if (openModal === true) {
            setOpenModal(false)
        }

        if (!storeCreditCardAlias.isInitializing) {
            getRemainingTime().then(time => {
                storeCreditCardAlias.setRemainingTime(time)
            })
        }

        if (['THA', 'SGP'].includes(StoreCountry.Country3())) {
            storeCreditCardAlias.setIsNewCard(true)
        }

        return () => {
            StoreSubScriptions.SetIsCancelOldSubscription(null)
            StoreSubScriptions.SetClickOpenSubscriptionDetectModal(false)
            StoreSubScriptions.SetAbleToShowSubscriptionDetectModal(false)
            StoreSubScriptions.SetDuplicateAutoship(null)
            StoreSubScriptions.SetIsCheckingDuplicateAutoship(false)
            StoreSubScriptions.SetHasCheckedDuplicateAutoship(false)
            StoreSubScriptions.SetDuplicateAutoshipCheckError(null)
            StoreSubScriptions.ResolveSubscriptionDetectModal(false)
            StoreCheckout.SetErrors([])
        }
    }, [])

    useEffect(() => {
        if (['payment', 'review'].includes(params.step)) {
            StoreCheckout.SetOrderSpecError([])
            if (params.step === 'payment') {
                storeCreditCardAlias.setRetryCount(0)
            }
        }
    }, [params.step])

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                left: 0
                // behavior: 'smooth'
            })
        }, 1)
    }, [isEmpty(StoreCheckout.GetCart())])

    const Sections = step => {
        switch (step) {
            case 'payment':
                return <PaymentBoxLazyLoad onPlaceOrder={onPlaceOrder} />
            case 'review':
                return <PaymentReviewLazyLoad />
            case 'create-account':
                return <CreateAccountWithShippingLazyLoad />
            default:
                return <ShippingAddressBoxLazyLoad />
        }
    }

    const CheckoutThemesSelector = () => {
        const countryLowerCase = StoreCountry.CountryLowerCase()
        const countryCode2 = StoreCountry.Country2()
        const themeProps = {
            onPlaceOrder,
            step: params.step,
            Sections,
            setOpenLoginModal,
            openLoginModal
        }

        const themeConfigMap = {
            egypt: { component: EgyptCheckoutTheme },
            hongkong: { component: HongkongCheckoutTheme, wrapWithStack: true },
            japan: { component: JapanCheckoutTheme },
            jordan: { component: JordanCheckoutTheme },
            kuwait: { component: KuwaitCheckoutTheme },
            morocco: { component: MoroccoCheckoutTheme },
            philippines: { component: PhillipinesCheckoutTheme },
            saudiarabia: { component: SaudiarabiaCheckoutTheme, wrapWithStack: true },
            southafrica: { component: SouthAfricaCheckoutTheme },
            taiwan: { component: TaiwanCheckoutTheme },
            thailand: { component: ThailandCheckoutTheme, wrapWithStack: true },
            turkiye: { component: TurkiyeCheckoutTheme },
        }

        const defaultConfig = { component: DefaultCheckoutTheme, wrapWithStack: true }
        const saudiConfig = countryLowerCase === 'saudiarabia' || countryCode2 === 'SA' ? themeConfigMap.saudiarabia : null
        const selectedConfig = saudiConfig || themeConfigMap[countryLowerCase] || defaultConfig
        const ThemeComponent = selectedConfig.component
        const content = <ThemeComponent {...themeProps} />

        if (selectedConfig.wrapWithStack) {
            return <StackStyled stepPaymentOrReview={params.step === 'review'}>{content}</StackStyled>
        }

        return content
    }

    const resetPayment = () => {
        setHydraCreatePaymentResponseBody(null)
        setHydraCreateOrderResponse(null)
        setHydraCreateOrderHeader(null)
        setHydraCreateOrderRequestBody(null)
        setPaymentId(null)
        setChallengeHTML(null)
        setOpenModal(true)
    }

    return (
        <ThemeProvider theme={MainTemplate}>
            <Stack backgroundColor={MainTemplate.palette.background.default} minHeight="100vh">
                <ContainerStyled maxWidth="lg">
                    <Stack display={!isEmpty(StoreCheckout.GetCart()) ? 'inherit' : 'none'}>
                        <Suspense
                            fallback={
                                <Stack>
                                    <PageSkeleton isLogin={StoreAuth.IsAuthorized()} />
                                </Stack>
                            }>
                            <Stack>
                                <Box
                                    mt="40px"
                                    height="84px"
                                    sx={{
                                        marginBottom: {
                                            xs: 0,
                                            lg: '20px'
                                        }
                                    }}>
                                    <Breadcrumb breadcrumbOption={StoreCheckout.CheckOutInstance().GetBreadcrumbOption()} />
                                </Box>
                            </Stack>
                            {CheckoutThemesSelector()}
                        </Suspense>
                    </Stack>

                    {isEmpty(StoreCheckout.GetCart()) && (
                        <Stack>
                            <CartEmpty />
                        </Stack>
                    )}
                </ContainerStyled>
            </Stack>
            <ProcessingModal
                open={openModal}
                onClose={() => setOpenModal(!openModal)}
                width={width}
                titleKey="hydra_payment_processing"
                steps={StorePaymentStep.GetPaymentSteps()}
                stepConfig={[
                    { key: 'payment' },
                    { key: 'createOrder' },
                    { key: 'autoship' },
                    { key: 'updateLog' }
                ]}
                errors={StorePaymentStep.PaymentErrors()}
                onErrorsClose={() => {
                    setOpenModal(false)
                    runInAction(() => {
                        StoreCheckout.checkoutLoading = false
                        storeElement.showCardFrontDrop = false
                    })
                }}
                closeButtonText={translate('CLOSE')}
            />
            <ProcessingModal
                open={openBankWireModal || StorePaymentStep.GetOpenModalBankWire()}
                onClose={() => {
                    setOpenBankWireModal(!openBankWireModal)
                    runInAction(() => {
                        StorePaymentStep.SetOpenModalBankWire(false)
                    })
                }}
                width={width}
                titleKey="hydra_payment_processing"
                steps={StorePaymentStep.GetPaymentSteps()}
                stepConfig={[
                    { key: 'createOrder', labelKey: 'bankwire_createOrder' },
                    { key: 'updateLog' }
                ]}
                errors={StorePaymentStep.PaymentErrors()}
                onErrorsClose={() => {
                    setOpenBankWireModal(false)
                    runInAction(() => {
                        StoreCheckout.checkoutLoading = false
                        storeElement.showCardFrontDrop = false
                        StorePaymentStep.SetOpenModalBankWire(false)
                    })
                }}
                closeButtonText={translate('CLOSE')}
            />
            <DialogDuplicatedStyled open={openDuplicatedModal}>
                <DialogContent
                    sx={{
                        padding: {
                            xs: '32px 24px',
                            md: '64px 93px'
                        }
                    }}>
                    <DuplicatedOrder confirmPlaceOrder={confirmPlaceOrder} setOpenModal={closeDuplicateModal} {...duplicatedData} />
                </DialogContent>
            </DialogDuplicatedStyled>
            {params.step === 'review' &&
                !isEmpty(challengeHTML) &&
                !isEmpty(hydraCreateOrderResponse) &&
                !isEmpty(hydraCreateOrderResponseHeader) &&
                !isEmpty(hydraCreateOrderRequestBody) &&
                !isEmpty(hydraCreatePaymentResponseBody) &&
                !isEmpty(paymentId) ? (
                <WorldPay3DS
                    challengeHTML={challengeHTML}
                    hydraCreateOrderResponseBody={hydraCreateOrderResponse}
                    hydraCreateOrderResponseHeader={hydraCreateOrderResponseHeader}
                    hydraCreateOrderRequestBody={hydraCreateOrderRequestBody}
                    paymentId={paymentId}
                    resetPayment={resetPayment}
                />
            ) : storeCreditCardAlias.getIsNewCard() === false && !isEmpty(StoreCheckout.cardAlias) ? (
                <WorldPay
                    open={openWorldPay}
                    onClose={() => {
                        setOpenWorldPay(false)
                    }}
                />
            ) : null}
        </ThemeProvider>
    )
})

const PageSkeleton = ({ isLogin = false }) => {
    return (
        <>
            <Stack>
                <Box
                    mt="40px"
                    height="84px"
                    sx={{
                        marginBottom: {
                            xs: 0,
                            lg: '20px'
                        }
                    }}>
                    <Box display={'flex'} flexDirection={'row'} justifyContent={'center'}>
                        {StoreCheckout.CheckOutInstance()
                            .GetBreadcrumbOption()(StoreCheckout.CheckOutInstance().GetBreadcrumbOption() || [])
                            .map((_, index) => (
                                <Box display={'flex'} flexDirection={'row'} justifyContent={'center'} key={`breadcrumb-skeleton-${index}`}>
                                    <Box>
                                        <Skeleton variant="circular" width={'54px'} height={'54px'} sx={{ margin: '0' }} />
                                        <Skeleton variant="rounded" width={'100%'} height={'10px'} sx={{ margin: '10px 0 10px 0' }} />
                                        <Skeleton variant="rounded" width={'100%'} height={'10px'} sx={{ margin: '10px 0 10px 0' }} />
                                    </Box>
                                    {index !== (StoreCheckout.CheckOutInstance().GetBreadcrumbOption() || []).length - 1 && (
                                        <Box width={(StoreCheckout.CheckOutInstance().GetBreadcrumbOption() || []).length < 4 ? { xs: '40px', sm: '120px', lg: '176px' } : { xs: '0px', sm: '100px', lg: '176px' }} mx={3}>
                                            <Skeleton variant="rounded" width={'100%'} height={'5px'} sx={{ marginTop: '27px' }} />
                                        </Box>
                                    )}
                                </Box>
                            ))}
                    </Box>
                </Box>
            </Stack>
            <PageSkeletonFormElement isLogin={isLogin} />
        </>
    )
}

const PageSkeletonFormElement = ({ isLogin = false }) => (
    <StackStyled>
        <Stack>
            {!isLogin && <Skeleton variant="rounded" width={'100%'} height={'64px'} sx={{ margin: '0 0 20px 0' }} />}
            <Skeleton variant="rounded" width={'100%'} height={'500px'} sx={{ margin: '0' }} />
        </Stack>
        <Stack>
            <Skeleton variant="rounded" width={'100%'} height={'370px'} sx={{ margin: '0' }} />
        </Stack>
    </StackStyled>
)

const ContainerStyled = styled(Container)`
    @media screen and (min-width: 830px) and (max-width: 1200px) {
        padding: 0 70px;
    }
`

const StackStyled = styled.div`
    padding-top: 40px;
    padding-bottom: 40px;
    min-height: 50vh;
    display: flex;
    flex-direction: ${props => (props.stepPaymentOrReview ? 'column' : 'column-reverse')};
    justify-content: ${props => (props.stepPaymentOrReview ? 'flex-start' : 'flex-end')};
    gap: 20px;

    @media screen and (min-width: 399px) {
        padding-top: 40px;
    }

    @media screen and (min-width: 600px) {
        padding-top: 60px;
    }

    @media screen and (min-width: 1200px) {
        display: grid;
        grid-template-columns: 770px 370px;
        gap: 22px;
        padding-top: 40px;
    }
`

const BackdropBodyStyled = styled.div`
    padding: 40px 24px;
    background: #003b6f;
    border-radius: 8px;

    @media screen and (min-width: 1024px) {
        width: 650px;
    }
`

const ProcessingStyled = styled.ul`
    margin-top: 40px;
    margin-bottom: 40px;
    padding-left: 0px;
    li {
        color: #ffffff;
        list-style-type: none;
        font-style: normal;
        font-weight: 300;
        font-size: 24px;
        padding-bottom: 20px;
        display: grid;
        grid-template-columns: auto 35px;
        align-items: center;
        padding-left: 0px;

        svg {
            width: 35px;
        }

        &:last-child {
            padding-bottom: 0px;
        }

        &.process-success {
            color: #03c302;
        }

        &.process-failed {
            color: rgb(254, 93, 124);
        }
    }
`

const Spiner = styled('img')({
    animation: 'rotation 1s infinite linear',
    '@keyframes rotation': {
        '0%': {
            transform: 'rotate(0deg)'
        },
        '100%': {
            transform: 'rotate(359deg)'
        }
    }
})

const DialogDuplicatedStyled = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        box-shadow: none;
        @media screen and (min-width: 768px) {
            width: 100%;
            max-width: 650px;
        }
    }
`

const createProcessingStepItems = (steps, stepConfig = []) => {
    if (!steps) {
        return []
    }

    return stepConfig
        .map(({ key, labelKey }) => {
            const value = steps[key]
            if (typeof value !== 'number' || value <= -2) {
                return null
            }

            return {
                key,
                labelKey: labelKey || key,
                value
            }
        })
        .filter(Boolean)
}

function PaymentStepTranslate(key, step) {
    let keyText = key
    if (key === 'payment') {
        keyText = 'loading_text_payment_data'
    } else if (key === 'createOrder') {
        keyText = 'loading_text_order_payment'
    } else if (key === 'updateLog') {
        keyText = 'loading_text_log'
    } else if (key === 'autoship') {
        keyText = 'loading_text_autoship'
    }

    const creatingText = 'loading_text_creating'
    const textSuccess = 'loading_text_success'
    const textFailed = 'loading_text_failed'

    switch (step) {
        case -1:
            return (
                <>
                    <Typography fontSize={'24px'} color="#ffffff">
                        <T>{keyText}</T> <T>{textFailed}</T>
                    </Typography>
                </>
            )
        case 0:
            return (
                <>
                    <Typography fontSize={'24px'} color="#ffffff">
                        <T>{creatingText}</T> <T>{keyText}</T>
                    </Typography>
                </>
            )
        case 1:
            return (
                <>
                    <Typography fontSize={'24px'} color="#ffffff">
                        <T>{keyText}</T> <T>{textSuccess}</T>
                    </Typography>
                </>
            )
        default:
            return <></>
    }
}

function ProcessingColor(step) {
    if (step === 1) {
        return 'process-success'
    } else if (step === -1) {
        return 'process-failed'
    }
}

function ProcessingIcon(step) {
    switch (step) {
        case 0:
            return <Spiner src={Loader} alt="Loading" style={{ marginLeft: 5 }} />
        case 1:
            return <CheckedIcon />
        case -1:
            return <TimesIcon />
        default:
            return <></>
    }
}

const ProcessingModal = observer(function ProcessingModal({
    open,
    onClose,
    width,
    titleKey,
    steps,
    stepConfig,
    errors = [],
    onErrorsClose,
    closeButtonText,
    disableEscapeKeyDown = true
}) {
    const processingItems = createProcessingStepItems(steps, stepConfig)

    return (
        <Modal
            disableEscapeKeyDown={disableEscapeKeyDown}
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Stack display={'flex'} alignItems={'center'} height="100%" justifyContent={'center'} padding={width < 730 ? '0 16px' : '0 47px'}>
                <BackdropBodyStyled>
                    <Typography variant="h2" textAlign={'left'} fontSize={'30px'} color="#ffffff">
                        <T>{titleKey}</T>
                    </Typography>
                    {processingItems.length > 0 && (
                        <ProcessingStyled>
                            {processingItems.map(item => (
                                <li key={item.key} className={ProcessingColor(item.value)}>
                                    {PaymentStepTranslate(item.labelKey, item.value)} {ProcessingIcon(item.value)}
                                </li>
                            ))}
                        </ProcessingStyled>
                    )}
                    {errors.length > 0 ? <Errors list={errors} /> : null}
                    {errors.length > 0 && onErrorsClose && closeButtonText ? (
                        <Stack mt="40px" display={'flex'} alignItems={'center'}>
                            <Box width="40%">
                                <DefaultButton titleText={closeButtonText} onClick={onErrorsClose} />
                            </Box>
                        </Stack>
                    ) : null}
                </BackdropBodyStyled>
            </Stack>
        </Modal>
    )
})

export default CheckoutPage
